import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../../../environments/environment';
import { SKIP_GLOBAL_403_REDIRECT, SKIP_SESSION_REAUTH } from '../http-context/skip-403-redirect.token';
import { TokenRefreshCoordinator } from '../services/token-refresh.coordinator';

/**
 * Interceptor funcional para injeção automática de Bearer token
 *
 * Responsabilidades:
 * - Adicionar Authorization header em requisições para apiBaseUrl
 * - Tratar erros 401 (não autenticado) tentando renovar token (mutex + falha limpa)
 * - Tratar erros 403 (não autorizado) redirecionando para página de erro
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const oidcSecurityService = inject(OidcSecurityService);
  const tokenRefresh = inject(TokenRefreshCoordinator);
  const router = inject(Router);

  // Só adiciona token para requisições às APIs do backend
  // chamado-service usa x-api-key (consulta-pm, fotos App 190) — Bearer PM invalida o JWT
  const isApiRequest = req.url.startsWith(environment.apiNexus) ||
                       req.url.startsWith(environment.apiNexusBase) ||
                       req.url.startsWith(environment.apiSisFrota) ||
                       req.url.startsWith(environment.apiPatrimonio) ||
                       req.url.startsWith(environment.atendimentoApi) ||
                       req.url.startsWith(environment.apiSigecad) ||
                       req.url.startsWith(environment.apiPlanus) ||
                       req.url.startsWith(environment.apiProvid ?? '') ||
                       req.url.startsWith(environment.apiVisus) ||
                       (req.url === environment.apiPraesto || req.url.startsWith(environment.apiPraesto + '/'));

  if (!isApiRequest) {
    return next(req);
  }

  const enviar = (token: string): Observable<HttpEvent<unknown>> => {
    const autenticada = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
    return next(autenticada).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (req.context.get(SKIP_SESSION_REAUTH) || tokenRefresh.isReauthInProgress()) {
            return throwError(() => error);
          }
          return renovarERepetir(req);
        }
        if (error.status === 403) {
          if (req.context.get(SKIP_GLOBAL_403_REDIRECT)) {
            return throwError(() => error);
          }
          router.navigate(['/acesso-negado']);
          return throwError(() => error);
        }
        return throwError(() => error);
      }),
    );
  };

  const renovarERepetir = (original: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
    tokenRefresh.refreshSession().pipe(
      switchMap(result => {
        if (!result.isAuthenticated) {
          return throwError(() => new HttpErrorResponse({ status: 401, url: original.url }));
        }
        return oidcSecurityService.getAccessToken().pipe(
          switchMap(novoToken => {
            if (!novoToken) {
              return throwError(() => new HttpErrorResponse({ status: 401, url: original.url }));
            }
            const retryReq = original.clone({
              setHeaders: { Authorization: `Bearer ${novoToken}` },
            });
            return next(retryReq);
          }),
        );
      }),
    );

  return oidcSecurityService.getAccessToken().pipe(
    switchMap(token => {
      if (token) {
        return enviar(token);
      }
      // Sem rede, não há como renovar: falha como erro de conectividade (status 0),
      // preservando o fluxo offline-first em vez de redirecionar ao Keycloak.
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return throwError(() => new HttpErrorResponse({ status: 0, url: req.url }));
      }
      if (tokenRefresh.isReauthInProgress()) {
        return throwError(() => new HttpErrorResponse({ status: 401, url: req.url }));
      }
      return renovarERepetir(req);
    }),
  );
};
